import type { ApplicationDraft } from "../../api/client";
import { Acknowledgement } from "../../components/Acknowledgement";
import { Callout } from "../../components/Callout";
import { TextField } from "../../components/TextField";

/**
 * Screen 07 · Agreements.
 *
 * Three acknowledgements and a name, and none of them is a preference. The platform terms
 * are the statement that Complao is a tool and the certification body retains full
 * responsibility for every audit outcome; the data processing acknowledgement names who is
 * controller and who is processor; authority to bind is the assertion that the person
 * submitting may commit the legal entity named in Step 1.
 *
 * That last one is why an administrator cannot submit an application on a certification
 * body's behalf. It is a signed assertion about the signer.
 *
 * Each acceptance is stored as its own row with the name that was typed and the moment it
 * was given, so a legal review (open item 7) that versions these paragraphs separately has
 * somewhere to put the version.
 */
export function AgreementsStep({
  draft,
  set,
  accreditationBody,
  errorFor,
}: {
  draft: ApplicationDraft;
  set: (patch: Partial<ApplicationDraft>) => void;
  /** Named in the closing callout, as screen 07 names JAS-ANZ. */
  accreditationBody: string;
  errorFor: (field: string) => string | undefined;
}) {
  return (
    <section className="card">
      <h2>Agreements</h2>

      <Acknowledgement
        id="platform_terms"
        title="Platform terms."
        checked={draft.platform_terms ?? false}
        error={errorFor("platform_terms")}
        onChange={(value) => set({ platform_terms: value })}
      >
        Complao is an audit tool. The certification body retains full responsibility for
        every audit outcome, certification decision and certificate issued. Nothing Complao
        produces constitutes an audit conclusion until a competent lead auditor has
        reviewed, amended as required, and signed it off.
      </Acknowledgement>

      <Acknowledgement
        id="data_processing"
        title="Data processing."
        checked={draft.data_processing ?? false}
        error={errorFor("data_processing")}
        onChange={(value) => set({ data_processing: value })}
      >
        The certification body is the controller of client data. Complao acts as processor
        and uses subprocessors for model inference, storage and email delivery. Client
        documents and audit transcripts are never used to train models.
      </Acknowledgement>

      <Acknowledgement
        id="authority_to_bind"
        title="Authority to bind."
        checked={draft.authority_to_bind ?? false}
        error={errorFor("authority_to_bind")}
        onChange={(value) => set({ authority_to_bind: value })}
      >
        I am authorised to enter this agreement on behalf of the legal entity named in
        Step 1, and the accreditation details supplied are true and current.
      </Acknowledgement>

      <div className="field" style={{ marginTop: "14px", maxWidth: "340px" }}>
        <TextField
          id="authority_name"
          label="Signed by"
          hint="full name"
          value={draft.authority_name ?? ""}
          onChange={(value) => set({ authority_name: value })}
          error={errorFor("authority_name")}
          required
        />
      </div>

      <Callout tone="warn" title="What happens after you submit.">
        Complao verifies your accreditation against{" "}
        {accreditationBody || "your accreditation body"}&rsquo;s public register before
        activating the account. You cannot configure an audit until that check passes.
      </Callout>
    </section>
  );
}
